import { useState, useRef, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
  WebSocketMessage,
  WebSocketResponse,
  WebSocketStatus,
  Tool,
  TransformationInfo,
  ToolInput,
} from '../types';

const isConnectionOpen = (state: ReadyState): boolean => {
  return state === ReadyState.OPEN;
};

interface UseTransformWebSocketProps {
  webSocketUrl: string;
  fileId: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setCategories: React.Dispatch<
    React.SetStateAction<Record<string, Tool[]> | undefined>
  >;
  explanations: Record<string, string> | undefined;
  setExplanations: React.Dispatch<
    React.SetStateAction<Record<string, string> | undefined>
  >;
  setBeforeHeaderData: React.Dispatch<
    React.SetStateAction<Record<string, string>[]>
  >;
  setHeaderData: React.Dispatch<React.SetStateAction<Record<string, string>[]>>;
  setAfterHeaderData: React.Dispatch<
    React.SetStateAction<Record<string, string>[]>
  >;
}

export const useTransformWebSocket = ({
  webSocketUrl,
  fileId,
  setLoading: setPageLoading,
  setCategories,
  explanations,
  setExplanations,
  setBeforeHeaderData,
  setHeaderData,
  setAfterHeaderData,
}: UseTransformWebSocketProps) => {
  const [loading, setLoading] = useState(false);
  const [isInPreviewMode, setIsInPreviewMode] = useState(false);
  const initialDataRef = useRef<Partial<TransformationInfo>>();
  const [appliedTransformations, setAppliedTransformations] = useState<
    TransformationInfo[]
  >([]);
  const [currentTransformation, setCurrentTransformation] =
    useState<TransformationInfo>();
  const sentToolInputsCache = useRef<
    Record<string, Record<string, ToolInput>[]>
  >({});

  const handleFileRetrieved = (response: WebSocketResponse) => {
    if (initialDataRef.current) {
      return;
    }
    setCategories(
      Object.values(response?.tool_list ?? []).reduce((result, current) => {
        result[current.schema.category] = result[current.schema.category] || [];
        result[current.schema.category].push(current);
        return result;
      }, {} as Record<string, Tool[]>)
    );

    setBeforeHeaderData(response.df_before_header ?? []);
    setHeaderData(
      response?.header
        ? [
            response?.header.reduce((result, col, index) => {
              result[index] = col;
              return result;
            }, {}),
          ]
        : []
    );
    setAfterHeaderData(response.df_after_header ?? []);
    setPageLoading(false);
    initialDataRef.current = {
      df_before_header: response.df_before_header ?? [],
      header: response?.header ?? [],
      df_after_header: response.df_after_header ?? [],
    };
  };

  const handleCancelTransformation = () => {
    if (appliedTransformations.length) {
      const currentTransformation =
        appliedTransformations[appliedTransformations.length - 1];
      setCurrentTransformation(currentTransformation);

      setBeforeHeaderData(currentTransformation?.df_before_header ?? []);
      setHeaderData(
        currentTransformation?.header
          ? [
              currentTransformation?.header.reduce((result, col, index) => {
                result[index] = col;
                return result;
              }, {}),
            ]
          : []
      );
      setAfterHeaderData(currentTransformation?.df_after_header ?? []);
    } else {
      setBeforeHeaderData(initialDataRef.current?.df_before_header ?? []);
      setHeaderData(
        initialDataRef.current?.header
          ? [
              initialDataRef.current?.header.reduce((result, col, index) => {
                result[index] = col;
                return result;
              }, {}),
            ]
          : []
      );
      setAfterHeaderData(initialDataRef.current?.df_after_header ?? []);
    }
    setIsInPreviewMode(false);
  };
  const handleApplyTransformation = () => {
    if (currentTransformation) {
      setAppliedTransformations((prev) => [
        ...prev,
        { ...currentTransformation },
      ]);
    }
    setIsInPreviewMode(false);
  };

  const handleTransformationApplied = (response: WebSocketResponse) => {
    const toolName = response?.selected_tool_name || '';
    let toolInputs: Record<string, ToolInput>[] = [];
    if (sentToolInputsCache.current[toolName]) {
      toolInputs = sentToolInputsCache.current[toolName];
    } else {
      toolInputs = response?.tool_input ?? [];
    }

    setCurrentTransformation({
      selected_tool_name: toolName,
      df_before_header: response?.df_before_header ?? [],
      header: response?.header ?? [],
      df_after_header: response?.df_after_header ?? [],
      tool_input: toolInputs,
    });

    setExplanations(response.explanation);
    setBeforeHeaderData(response.df_before_header ?? []);
    setHeaderData(
      response?.header
        ? [
            response?.header.reduce((result, col, index) => {
              result[index] = col;
              return result;
            }, {}),
          ]
        : []
    );
    setAfterHeaderData(response.df_after_header ?? []);
    setIsInPreviewMode(true);
  };

  const handleSuggestionGenerated = (response: WebSocketResponse) => {
    setExplanations(response.explanation);
  };

  const handleStopLoading = () => {
    setLoading(false);
    setPageLoading(false);
  };

  const { sendJsonMessage, lastJsonMessage, getWebSocket, readyState } =
    useWebSocket(webSocketUrl, {
      onOpen: () => {
        if (!initialDataRef.current) {
          sendJsonMessage({
            status: WebSocketStatus.FILE_ID_SUBMITTED,
            file_id: fileId,
          });
        }
      },
      onMessage: (event) => {
        const message = JSON.parse(event.data) as WebSocketResponse;

        switch (message.status) {
          case WebSocketStatus.FILE_RETREIVED:
            handleFileRetrieved(message);
            break;
          case WebSocketStatus.TRANSFORMATION_APPLIED:
            setLoading(false);
            handleTransformationApplied(message);
            break;
          case WebSocketStatus.SUGGESTION_GENERATED:
            handleSuggestionGenerated(message);
            break;
          case WebSocketStatus.REQUEST_CANCELLED:
            handleStopLoading();
            break;
          case WebSocketStatus.ERROR:
            handleStopLoading();
        }
      },
      onError: (_event) => {
        handleStopLoading();
      },
      shouldReconnect: () => true,
      retryOnError: true,
      reconnectAttempts: 10,
      reconnectInterval: 3000,
    });

  const sendJsonMessageSafely = useCallback(
    async (message: any): Promise<void> => {
      if (isConnectionOpen(readyState)) {
        sendJsonMessage(message);
        return;
      }
      return new Promise((resolve, reject) => {
        const maxWaitTime = 10000;
        const checkInterval = 100;
        let elapsedTime = 0;

        const timer = setInterval(() => {
          elapsedTime += checkInterval;
          if (isConnectionOpen(readyState)) {
            clearInterval(timer);
            sendJsonMessage(message);
            resolve();
          } else if (elapsedTime >= maxWaitTime) {
            clearInterval(timer);
            reject(
              new Error(`WebSocket connection timed out after ${maxWaitTime}ms`)
            );
          }
        }, checkInterval);
      });
    },
    [readyState, sendJsonMessage]
  );

  const requestSuggestion = useCallback(
    async (toolName: string, currentState: Partial<WebSocketMessage>) => {
      const message = {
        status: WebSocketStatus.REQUEST_SUGGESTION,
        file_id: fileId,
        selected_tool_name: toolName,
        ...currentState,
        explanation: explanations ?? {},
      };

      try {
        await sendJsonMessageSafely(message);
      } catch (_error) {
        handleStopLoading();
      }
    },
    [fileId, explanations, sendJsonMessageSafely]
  );

  const applyToolTransform = useCallback(
    async (toolName: string, currentState: Partial<WebSocketMessage>) => {
      setLoading(true);
      sentToolInputsCache.current[toolName] = currentState.tool_input || [];
      const message = {
        status: WebSocketStatus.APPLY_TOOL_TRANSFORM,
        file_id: fileId,
        selected_tool_name: toolName,
        ...currentState,
        explanation: explanations ?? {},
      };

      try {
        await sendJsonMessageSafely(message);
      } catch (_error) {
        handleStopLoading();
      }
    },
    [fileId, explanations, sendJsonMessageSafely, setLoading]
  );

  const handleCancel = () => {
    const message = {
      status: WebSocketStatus.CANCEL_REQUEST,
      file_id: fileId,
    };
    sendJsonMessageSafely(message);
  };

  const closeConnection = () => {
    const ws = getWebSocket();
    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      ws.close();
    }
  };

  return {
    loading,
    lastJsonMessage,
    requestSuggestion,
    applyToolTransform,
    closeConnection,
    appliedTransformations,
    handleCancelTransformation,
    handleApplyTransformation,
    handleCancel,
    isInPreviewMode,
  };
};
